import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/ui/Header';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import PersonalDetails from './components/PersonalDetails';
import TravelPreferences from './components/TravelPreferences';
import NotificationSettings from './components/NotificationSettings';
import PrivacySettings from './components/PrivacySettings';
import TravelDocuments from './components/TravelDocuments';
import SecuritySettings from './components/SecuritySettings';

const ProfileManagement = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('personal');
  const [isEditing, setIsEditing] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  // User profile state
  const [profileData, setProfileData] = useState({
    personalInfo: {
      firstName: "Sarah",
      lastName: "Johnson",
      email: "sarah.johnson@email.com",
      phone: "+1 (555) 123-4567",
      dateOfBirth: "1990-03-15",
      nationality: "United States",
      address: {
        street: "123 Main Street",
        city: "New York",
        state: "NY",
        postalCode: "10001",
        country: "United States"
      },
      emergencyContact: {
        name: "John Johnson",
        relationship: "Brother",
        phone: "+1 (555) 987-6543",
        email: "john.johnson@email.com"
      },
      profileImage: "https://images.unsplash.com/photo-1494790108755-2616b612b786?w=150&h=150&fit=crop&crop=face",
      memberSince: "2022-03-15",
      specialRequirements: "Vegetarian meals, Wheelchair accessible accommodations"
    },
    travelPreferences: {
      destinationInterests: ["Cultural Heritage", "Adventure", "Beach", "Mountains", "Urban Exploration"],
      activityTypes: ["Museums", "Hiking", "Food Tours", "Photography", "Wildlife"],
      budgetRange: {
        min: 1000,
        max: 5000,
        currency: "USD"
      },
      accommodationPreference: "Mid-range Hotels",
      travelStyle: "Group Tours",
      accessibilityRequirements: ["Wheelchair Access", "Hearing Assistance"],
      dietaryRestrictions: ["Vegetarian"],
      languagePreferences: ["English", "Spanish"],
      favoriteDestinations: ["Japan", "Italy", "Greece", "Thailand"],
      travelFrequency: "3-4 times per year"
    },
    notifications: {
      emailNotifications: {
        bookingConfirmations: true,
        paymentReceipts: true,
        tripReminders: true,
        promotionalOffers: false,
        newsletterUpdates: true,
        priceDropAlerts: true,
        weatherUpdates: false
      },
      smsNotifications: {
        bookingConfirmations: true,
        paymentReceipts: false,
        tripReminders: true,
        emergencyAlerts: true,
        lastMinuteDeals: false
      },
      pushNotifications: {
        bookingUpdates: true,
        checkInReminders: true,
        localRecommendations: true,
        safetyAlerts: true,
        promotionalOffers: false
      },
      frequency: "immediate",
      quietHours: {
        enabled: true,
        startTime: "22:00",
        endTime: "08:00"
      }
    },
    privacy: {
      profileVisibility: "private",
      dataSharing: {
        analytics: false,
        marketing: false,
        thirdPartyPartners: false,
        socialMediaIntegration: false
      },
      locationSharing: false,
      reviewVisibility: "public",
      contactFromOtherTravelers: false,
      showTravelHistory: false,
      marketingCommunications: false
    },
    documents: {
      passport: {
        number: "US123456789",
        issueDate: "2020-01-15",
        expiryDate: "2030-01-15",
        issuingCountry: "United States",
        status: "active"
      },
      visas: [
        {
          id: "visa-1",
          country: "Japan",
          type: "Tourist Visa",
          validFrom: "2024-01-01",
          validUntil: "2025-01-01",
          status: "active"
        }
      ],
      travelInsurance: {
        provider: "Global Travel Insurance",
        policyNumber: "GTI-2024-789123",
        coverage: "$100,000",
        validFrom: "2024-01-01",
        validUntil: "2024-12-31",
        status: "active"
      },
      vaccinations: [
        {
          id: "vac-1",
          name: "COVID-19",
          date: "2023-09-15",
          validUntil: "2024-09-15",
          certificate: true
        },
        {
          id: "vac-2",
          name: "Yellow Fever",
          date: "2023-03-20",
          validUntil: "2033-03-20",
          certificate: true
        }
      ]
    },
    security: {
      lastPasswordChange: "2024-08-15",
      twoFactorEnabled: true,
      twoFactorMethod: "authenticator",
      loginHistory: [
        {
          id: "login-1",
          date: "2024-11-18T14:30:00Z",
          location: "New York, NY",
          device: "Chrome Browser",
          ipAddress: "192.168.1.100"
        },
        {
          id: "login-2",
          date: "2024-11-17T09:15:00Z",
          location: "New York, NY",
          device: "iPhone Safari",
          ipAddress: "192.168.1.101"
        }
      ],
      trustedDevices: [
        {
          id: "device-1",
          name: "Sarah\'s MacBook Pro",
          lastUsed: "2024-11-18T14:30:00Z",
          location: "New York, NY"
        }
      ]
    }
  });

  const tabs = [
    { id: 'personal', label: 'Personal Details', icon: 'User' },
    { id: 'travel', label: 'Travel Preferences', icon: 'Compass' },
    { id: 'notifications', label: 'Notifications', icon: 'Bell' },
    { id: 'privacy', label: 'Privacy', icon: 'Shield' },
    { id: 'documents', label: 'Travel Documents', icon: 'FileText' },
    { id: 'security', label: 'Security', icon: 'Lock' }
  ];

  // Handle form submission
  const handleSaveProfile = async () => {
    try {
      // API call simulation
      console.log('Saving profile data:', profileData);
      setHasUnsavedChanges(false);
      setIsEditing(false);
      // Show success notification
    } catch (error) {
      console.error('Error saving profile:', error);
      // Show error notification
    }
  };

  // Handle data export
  const handleDataExport = () => {
    const dataStr = JSON.stringify(profileData, null, 2);
    const dataUri = 'data:application/json;charset=utf-8,'+ encodeURIComponent(dataStr);
    
    const exportFileDefaultName = `profile-data-${new Date()?.toISOString()?.split('T')?.[0]}.json`;
    
    const linkElement = document.createElement('a');
    linkElement?.setAttribute('href', dataUri);
    linkElement?.setAttribute('download', exportFileDefaultName);
    linkElement?.click();
  };

  // Handle account deletion
  const handleDeleteAccount = () => {
    if (window.confirm('Are you sure you want to delete your account? This action cannot be undone.')) {
      // API call to delete account
      console.log('Account deletion requested');
      navigate('/authentication');
    }
  };

  const updateProfileData = (section, data) => {
    setProfileData(prev => ({
      ...prev,
      [section]: { ...prev?.[section], ...data }
    }));
    setHasUnsavedChanges(true);
  };

  const renderTabContent = () => {
    switch (activeTab) {
      case 'personal':
        return (
          <PersonalDetails 
            data={profileData?.personalInfo} 
            onUpdate={(data) => updateProfileData('personalInfo', data)}
            isEditing={isEditing}
            setIsEditing={setIsEditing}
          />
        );
      
      case 'travel':
        return (
          <TravelPreferences 
            data={profileData?.travelPreferences} 
            onUpdate={(data) => updateProfileData('travelPreferences', data)}
            isEditing={isEditing}
            setIsEditing={setIsEditing}
          />
        );
      
      case 'notifications':
        return (
          <NotificationSettings 
            data={profileData?.notifications} 
            onUpdate={(data) => updateProfileData('notifications', data)}
          />
        );
      
      case 'privacy':
        return (
          <PrivacySettings 
            data={profileData?.privacy} 
            onUpdate={(data) => updateProfileData('privacy', data)}
          />
        );
      
      case 'documents':
        return (
          <TravelDocuments 
            data={profileData?.documents} 
            onUpdate={(data) => updateProfileData('documents', data)}
            isEditing={isEditing}
            setIsEditing={setIsEditing}
          />
        );
      
      case 'security':
        return (
          <SecuritySettings 
            data={profileData?.security} 
            onUpdate={(data) => updateProfileData('security', data)}
          />
        );
      
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <div className="max-w-7xl mx-auto px-4 lg:px-6 py-8">
        {/* Page Header */}
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between mb-8">
          <div className="mb-4 lg:mb-0">
            <div className="flex items-center space-x-4">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate(-1)}
                iconName="ArrowLeft"
                className="mb-2"
              >
                Back
              </Button>
            </div>
            <h1 className="text-3xl font-bold text-foreground mb-2">Profile Management</h1>
            <p className="text-muted-foreground">
              Manage your personal information, preferences, and account settings
            </p>
          </div>
          
          {/* Action Buttons */}
          <div className="flex items-center space-x-4">
            {hasUnsavedChanges && (
              <div className="flex items-center space-x-2">
                <Icon name="AlertCircle" size={16} className="text-warning" />
                <span className="text-sm text-warning">Unsaved changes</span>
              </div>
            )}
            <Button
              variant="outline"
              size="sm"
              onClick={handleDataExport}
              iconName="Download"
            >
              Export Data
            </Button>
            {hasUnsavedChanges && (
              <Button
                size="sm"
                onClick={handleSaveProfile}
                iconName="Save"
              >
                Save Changes
              </Button>
            )}
          </div>
        </div>

        {/* Profile Overview Card */}
        <div className="bg-card rounded-lg p-6 mb-8 border">
          <div className="flex flex-col sm:flex-row items-center space-y-4 sm:space-y-0 sm:space-x-6">
            <div className="relative">
              <div className="w-24 h-24 rounded-full overflow-hidden border-4 border-primary/20">
                <img
                  src={profileData?.personalInfo?.profileImage}
                  alt={`${profileData?.personalInfo?.firstName} ${profileData?.personalInfo?.lastName}`}
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    e.target.src = '/assets/images/no_image.png';
                  }}
                />
              </div>
              <button className="absolute bottom-0 right-0 w-8 h-8 bg-primary text-primary-foreground rounded-full flex items-center justify-center hover:bg-primary/90 transition-colors">
                <Icon name="Camera" size={14} />
              </button>
            </div>
            
            <div className="text-center sm:text-left flex-1">
              <h2 className="text-2xl font-bold text-foreground">
                {profileData?.personalInfo?.firstName} {profileData?.personalInfo?.lastName}
              </h2>
              <p className="text-muted-foreground mb-2">{profileData?.personalInfo?.email}</p>
              <div className="flex flex-col sm:flex-row sm:items-center sm:space-x-4 space-y-1 sm:space-y-0 text-sm text-muted-foreground">
                <span>Member since {new Date(profileData?.personalInfo?.memberSince)?.getFullYear()}</span>
                <span className="hidden sm:inline">•</span>
                <span>12 trips completed</span>
                <span className="hidden sm:inline">•</span>
                <span>8 countries visited</span>
              </div>
            </div>
          </div>
        </div>

        {/* Tab Navigation */}
        <div className="border-b border-border mb-8">
          <div className="flex space-x-1 overflow-x-auto">
            {tabs?.map((tab) => (
              <button
                key={tab?.id}
                onClick={() => setActiveTab(tab?.id)}
                className={`flex items-center space-x-2 px-4 py-3 text-sm font-medium rounded-t-lg transition-colors whitespace-nowrap ${
                  activeTab === tab?.id
                    ? 'text-primary border-b-2 border-primary bg-primary/5' :'text-muted-foreground hover:text-foreground hover:bg-muted/50'
                }`}
              >
                <Icon name={tab?.icon} size={16} />
                <span>{tab?.label}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Tab Content */}
        <div className="animate-fade-in">
          {renderTabContent()}
        </div>

        {/* Danger Zone */}
        <div className="mt-16 pt-8 border-t border-destructive/20">
          <div className="bg-destructive/5 rounded-lg p-6 border border-destructive/20">
            <h3 className="text-lg font-semibold text-destructive mb-4 flex items-center">
              <Icon name="AlertTriangle" size={20} className="mr-2" />
              Danger Zone
            </h3>
            <div className="space-y-4">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
                <div>
                  <p className="font-medium text-foreground">Delete Account</p>
                  <p className="text-sm text-muted-foreground">
                    Permanently delete your account and all associated data
                  </p>
                </div>
                <Button
                  variant="destructive"
                  size="sm"
                  onClick={handleDeleteAccount}
                  className="mt-2 sm:mt-0"
                >
                  Delete Account
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProfileManagement;